// Ordered method list with reorder + remove.
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import type { ContactMethod, MethodKey } from '../mockData.js';

export interface MethodListProps {
  methods: ContactMethod[];
  order: MethodKey[];
  onReorder: (next: MethodKey[]) => void;
  onRemove: (key: MethodKey) => void;
}

export function MethodList({
  methods,
  order,
  onReorder,
  onRemove,
}: MethodListProps): ReactNode {
  function move(index: number, delta: number): void {
    const next = [...order];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onReorder(next);
  }
  return (
    <ol id="ep-method-list" className="ep-method-list" aria-label="Contact methods ranked by preference">
      {order.map((key, index) => {
        const method = methods.find(m => m.key === key);
        if (!method) return null;
        return (
          <li key={key} className={`ep-method-item ep-method-item-${index + 1}`}>
            <span className="ep-method-rank">{index + 1}</span>
            <div className="ep-method-body">
              <strong>{method.label}</strong>
              <small>{method.caption}</small>
            </div>
            <div className="ep-method-actions">
              <button
                type="button"
                className="ep-icon-btn"
                aria-label={`Move ${method.label} up`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="ep-icon-btn"
                aria-label={`Move ${method.label} down`}
                disabled={index === order.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="ep-link"
                onClick={() => onRemove(key)}
                aria-label={`Remove ${method.label}`}
              >
                Remove
              </button>
              {method.permission === 'granted' ? (
                <span className="ep-permission-pill granted">
                  <Icon id="check" /> Permission granted
                </span>
              ) : method.permission === 'needed' ? (
                <span className="ep-permission-pill needed">Permission needed</span>
              ) : (
                <span className="ep-permission-pill blocked">Blocked</span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
